// External dependencies
import React from 'react';
// WordPress dependencies
import { Component } from '@wordpress/element';
import { Spinner } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
// Internal dependencies
import CarouselTabs from './CarouselTabs';

class CarouselEditPost extends Component {
	constructor( props ) {
		super( props );

		this.state = {
			is_mounted: false,
			post_id: document.querySelector( '#post_ID' ).value,
			active_tab: 'sliders',
			
			// Slider selection
			selected_slider_ids: [],
			
			// Basic Settings
			speed: 300,
			loop: true,
			
			// Autoplay Settings
			autoplay: false,
			autoplay_delay: 3000,
			pause_on_hover: true,
			
			// Navigation Settings
			navigation: true,
			pagination: true,
			pagination_type: 'bullets',

			// Navigation Styles
			nav_bg_color: '',
			nav_arrow_color: '',
			nav_hover_bg_color: '',
			nav_hover_arrow_color: '',

			// Pagination Styles
			pag_bg_color: '',
			pag_active_color: '',
			pag_hover_color: '',

			// UI state for color popovers
			customToggles: {},
			
			// Responsive Breakpoints - Mobile
			breakpoint_mobile: 640,
			slides_mobile: 1,
			space_mobile: 10,
			
			// Responsive Breakpoints - Tablet
			breakpoint_tablet: 768,
			slides_tablet: 2,
			space_tablet: 20,
			
			// Responsive Breakpoints - Desktop
			breakpoint_desktop: 1024,
			slides_desktop: 3,
			space_desktop: 30,
		};

		this.proFeatures = [
			'autoplay',
			'autoplay_delay',
			'pause_on_hover',
			'pagination_type',
		];

		this.defaultProSettings = {
			is_pro_active: false,
		};
		this.proSettings = window.COCA_BAIS_PRO || this.defaultProSettings;
		this.buyNowUrl = 'https://codecanel.com/wp-before-after-image-slider/';
		this.handleUpdateAttributes = this.handleUpdateAttributes.bind( this );
	}

	/**
	 * Normalize loaded data - wp_localize_script converts types oddly
	 * PHP booleans become strings "1" or "", integers become strings
	 */
	normalizeLoadedData( data ) {
		const booleanFields = [ 'loop', 'autoplay', 'pause_on_hover', 'navigation', 'pagination' ];
		const integerFields = [
			'speed', 'autoplay_delay',
			'breakpoint_mobile', 'breakpoint_tablet', 'breakpoint_desktop',
			'slides_mobile', 'slides_tablet', 'slides_desktop',
			'space_mobile', 'space_tablet', 'space_desktop',
		];

		const normalized = { ...data };

		booleanFields.forEach( ( field ) => {
			if ( field in normalized ) {
				// PHP true becomes "1", false becomes "" or "0"
				normalized[ field ] = normalized[ field ] === true || normalized[ field ] === '1' || normalized[ field ] === 1;
			}
		} );

		integerFields.forEach( ( field ) => {
			if ( field in normalized ) {
				const parsed = parseInt( normalized[ field ], 10 );
				normalized[ field ] = isNaN( parsed ) ? this.state[ field ] : parsed;
			}
		} );

		// Ensure selected_slider_ids is an array
		if ( normalized.selected_slider_ids && ! Array.isArray( normalized.selected_slider_ids ) ) {
			normalized.selected_slider_ids = Object.values( normalized.selected_slider_ids );
		}

		return normalized;
	}

	componentDidMount() {
		// Try multiple sources for active tab preservation
		let preservedActiveTab = null;
		
		// First, check URL parameters for active tab
		const urlParams = new URLSearchParams( window.location.search );
		const urlActiveTab = urlParams.get( 'active_tab' );
		
		// Second, check localStorage before clearing
		const cachedData = window.localStorage.getItem( 'coca_bais_carousel_data' );
		if ( cachedData ) {
			try {
				const parsedData = JSON.parse( cachedData );
				preservedActiveTab = parsedData.active_tab;
			} catch ( e ) {
				// If parsing fails, ignore the cached data
			}
		}
		
		// Third, check sessionStorage as backup
		const sessionData = window.sessionStorage.getItem( 'coca_bais_carousel_active_tab' );
		
		// Clean local cache
		window.localStorage.removeItem( 'coca_bais_carousel_data' );
		
		// Data from database - normalize types
		const rawMetadata = window.COCA_BAIS_CAROUSEL_META_DATA || {};
		const savedMetadata = this.normalizeLoadedData( rawMetadata );
		
		// Determine which active_tab to use (priority order: URL > localStorage > sessionStorage > database)
		if ( urlActiveTab && [ 'sliders', 'settings' ].includes( urlActiveTab ) ) {
			savedMetadata.active_tab = urlActiveTab;
		} else if ( preservedActiveTab && [ 'sliders', 'settings' ].includes( preservedActiveTab ) ) {
			savedMetadata.active_tab = preservedActiveTab;
		} else if ( sessionData && [ 'sliders', 'settings' ].includes( sessionData ) ) {
			savedMetadata.active_tab = sessionData;
		}
		
		// Store current active tab in sessionStorage for persistence
		if ( savedMetadata.active_tab ) {
			window.sessionStorage.setItem( 'coca_bais_carousel_active_tab', savedMetadata.active_tab );
		}
		
		this.setState( ( prevState ) => ( {
			...prevState,
			...savedMetadata,
			is_mounted: true,
		} ) );
		
		// Add event listener to preserve tab state before page unload
		window.addEventListener( 'beforeunload', this.handleBeforeUnload );
	}
	
	// Save current active tab before page unload (for Update button clicks)
	handleBeforeUnload = () => {
		if ( this.state.active_tab ) {
			window.sessionStorage.setItem( 'coca_bais_carousel_active_tab', this.state.active_tab );
		}
	}
	
	// Cleanup event listener
	componentWillUnmount() {
		window.removeEventListener( 'beforeunload', this.handleBeforeUnload );
	}

	// Update all attributes
	handleUpdateAttributes( attributes ) {
		// First update the state
		this.setState( ( prevState ) => {
			const newState = {
				...prevState,
				...attributes,
			};
			
			// Then update localStorage with the complete updated state
			window.localStorage.setItem(
				'coca_bais_carousel_data',
				JSON.stringify( newState )
			);
			
			// Also store active_tab in sessionStorage for form submission persistence
			if ( attributes.active_tab ) {
				window.sessionStorage.setItem( 'coca_bais_carousel_active_tab', attributes.active_tab );
			}
			
			return newState;
		} );
	}

	render() {
		if ( this.state.is_mounted ) {
			return (
				<div className={ 'carousel-panel-wrapper' }>
					<CarouselTabs
						updateAttributes={ this.handleUpdateAttributes }
						attributes={ this.state }
						proSettings={ this.proSettings }
						proFeatureEnabled={ this.proSettings.is_pro_active }
						proFeatures={ this.proFeatures }
						buyPro={ this.buyNowUrl }
					/>
				</div>
			);
		}

		return (
			<div className="coca-bais-loading">
				<Spinner />
				<p>{ __( 'Loading carousel settings...', 'wp-before-after-image-slider' ) }</p>
			</div>
		);
	}
}

export default CarouselEditPost;
